import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import TrustStrip from '../components/TrustStrip';
import PropertyCard from '../components/PropertyCard';
import { DoorOpening, BookLayout } from '../components/BookLayout';
import { Button } from '../components/ui/button';
import { ArrowRight, Sparkles, MapPin, Shield, Award, Search, CreditCard, UserCheck, KeyRound, Home, Key, Building2, Briefcase, CheckCircle, MessageSquare, FileCheck, Users, XCircle, Wand2, Gift, Scale, TrendingUp, Bell } from 'lucide-react';
import { useCountry } from '../contexts/CountryContext';
import { fireWaInboundBeaconIfApplicable } from '../utils/waInboundBeacon';
import { useAuth } from '../contexts/AuthContext';
import { useSEO } from '../utils/seo';
import { TestimonialsSection, SavingsCalculator, TrustSeals, LandingCTA, LandingFooter, WhyShopperdenSection, AdBannersSection } from '../components/LandingSections';
import FindProfessionalPromo from '../components/FindProfessionalPromo';
import TrustpilotWidget from '../components/TrustpilotWidget';
import GovtLinksPanel from '../components/GovtLinksPanel';
import AppDownloadBanner from '../components/AppDownloadBanner';
import FreshLeadsCarousel from '../components/FreshLeadsCarousel';
import { HowItWorksFlows, usePlatformFees } from '../components/HowItWorksFlows';
import AINetworkTab from '../components/AINetworkTab';
import GotOurWhatsAppBanner from '../components/GotOurWhatsAppBanner';
import logger from '../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const LandingPage = () => {
  const [featuredProperties, setFeaturedProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sellerContent, setSellerContent] = useState(null);
  const [proContent, setProContent] = useState(null);
  const [whyContent, setWhyContent] = useState(null);
  const [adBanners, setAdBanners] = useState([]);
  const [adsenseConfig, setAdsenseConfig] = useState(null);
  const [liveStats, setLiveStats] = useState({ properties: 0, users: 0, countries: 3, verified_users: 0 });
  const [calcPrice, setCalcPrice] = useState('');
  const fees = usePlatformFees();
  const { user } = useAuth();
  // Estate-agent-only CTA visibility. Keep the section invisible for
  // every other audience (buyers, tenants, sellers, professionals,
  // anonymous visitors) so the hero stays clean for non-agent personas.
  const isEstateAgent = (user?.role || '').toLowerCase() === 'agent';

  useSEO({
    title: 'Shopperden — The Global AI Real Estate Network for Buyers & Sellers',
    description: 'Universal AI Real Estate Network connecting UK, India and Canada. AI-Powered Search Discovery for local and international buyers, sellers and Technology Service Partners. No agency fees — pay only for AI matchmaking & data products.',
    canonical: 'https://shopperden.com/',
    schema: {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": "Shopperden",
      "url": "https://shopperden.com",
      "description": "AI Real Estate Network — AI-Powered Search Discovery, market intelligence and Pulse scores. Not an estate agent or brokerage.",
      "publisher": {
        "@type": "Organization",
        "name": "Shopperden Ltd",
        "url": "https://shopperden.com"
      },
      "potentialAction": {
        "@type": "SearchAction",
        "target": "https://shopperden.com/properties?search={search_term_string}",
        "query-input": "required name=search_term_string"
      }
    }
  });

  const [doorComplete, setDoorComplete] = useState(() => {
    return sessionStorage.getItem('shopperden_door') === '1';
  });

  const handleDoorComplete = useCallback(() => {
    setDoorComplete(true);
    sessionStorage.setItem('shopperden_door', '1');
  }, []);

  const fetchLiveStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/stats`);
      setLiveStats(res.data);
    } catch (err) { logger.error('Failed to load stats:', err); }
  }, []);

  const fetchFeaturedProperties = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/properties/featured`);
      setFeaturedProperties(response.data);
    } catch (error) {
      logger.error('Error fetching featured properties:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSellerContent = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/landing-content`);
      setSellerContent(response.data?.seller_section || null);
      setProContent(response.data?.professional_section || null);
      setWhyContent(response.data?.why_shopperden_section || null);
    } catch (error) {
      logger.error('Error fetching landing content:', error);
    }
  }, []);

  const fetchAds = useCallback(async () => {
    try {
      const [bannersRes, adsenseRes] = await Promise.all([
        axios.get(`${API_URL}/api/ads/banners`),
        axios.get(`${API_URL}/api/ads/adsense-config`),
      ]);
      setAdBanners(bannersRes.data || []);
      setAdsenseConfig(adsenseRes.data || null);
    } catch (error) {
      logger.error('Failed to load ads:', error);
    }
  }, []);

  useEffect(() => {
    fetchFeaturedProperties();
    fetchSellerContent();
    fetchAds();
    fetchLiveStats();
    // WhatsApp pro-blast click attribution — only fires for mobile
    // visitors landing on `/` with no referrer (heuristic for an
    // in-app WhatsApp link tap). See utils/waInboundBeacon.js.
    fireWaInboundBeaconIfApplicable();
  }, [fetchFeaturedProperties, fetchSellerContent, fetchAds, fetchLiveStats]);

  // Auto-refresh live stats counter every 30s + when tab regains focus,
  // so registered-users / live-properties always reflect current DB.
  useEffect(() => {
    const interval = setInterval(fetchLiveStats, 30000);
    const onFocus = () => fetchLiveStats();
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
    };
  }, [fetchLiveStats]);

  // Load Soro blog embed on landing page
  useEffect(() => {
    const container = document.getElementById('soro-blog-landing');
    if (!container) return;
    const params = new URLSearchParams(window.location.search);
    let url = 'https://app.trysoro.com/api/embed/24f1e95d-da9c-46ef-ae2f-7c2a572cb22c';
    if (params.get('post')) url += '?post=' + encodeURIComponent(params.get('post'));
    const script = document.createElement('script');
    script.src = url;
    container.appendChild(script);
    return () => { if (container) { while (container.firstChild) container.removeChild(container.firstChild); } };
  }, []);

  const trackBannerClick = async (bannerId) => {
    try {
      await axios.post(`${API_URL}/api/ads/banners/${bannerId}/click`);
    } catch (error) {
      logger.error('Failed to track click:', error);
    }
  };

  /* ───── PAGE 1: HOME (Hero + Features) ───── */
  const page1 = (
    <div>
      {/* Free Trial Banner */}
      {fees.freeTrial && (
        <Link to="/register" className="block bg-gradient-to-r from-emerald-500 to-green-600 text-white py-3 px-4 text-center hover:from-emerald-600 hover:to-green-700 transition-all cursor-pointer" data-testid="free-trial-banner">
          <div className="max-w-7xl mx-auto flex items-center justify-center gap-3 flex-wrap">
            <Gift className="h-5 w-5 animate-bounce" />
            <span className="font-semibold text-sm sm:text-base tracking-wider uppercase">
              Free Trial Active — All Platform Fees Waived!
            </span>
            <span className="bg-white text-green-700 px-4 py-1 text-xs font-bold tracking-wider uppercase hover:bg-green-50 transition-colors">
              Sign Up Free
            </span>
          </div>
        </Link>
      )}

      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('https://images.unsplash.com/photo-1760129745103-91c4022ed5fb?w=1920&auto=format&fit=crop')` }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-white/85 via-white/75 to-[#FEF3EC]/70" />
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-[0.06] pointer-events-none"
          style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, #1A1A1A 1px, transparent 0)',
            backgroundSize: '28px 28px',
          }}
        />

        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-20 w-full">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/70 backdrop-blur-sm border border-[#E07A3A]/30 shadow-sm mb-7 animate-fade-in-up">
              <span className="w-1.5 h-1.5 rounded-full bg-[#E07A3A] animate-pulse" />
              <p className="text-[11px] tracking-[0.3em] uppercase text-[#C4652E] font-bold">
                {fees.freeTrial ? 'Free Trial · Limited Time' : 'AI Real Estate Network'}
              </p>
            </div>
            <h1 className="font-['Playfair_Display'] font-semibold leading-[1.02] tracking-tight text-[#1A1A1A] mb-8 animate-fade-in-up stagger-1 text-[2.5rem] sm:text-6xl lg:text-[5rem]">
              <span className="block">The Global</span>
              <span className="block italic text-[#E07A3A]">AI Real Estate</span>
              <span className="block">Network</span>
            </h1>
            <p className="text-[#1A1A1A]/70 text-base sm:text-xl leading-relaxed mb-10 max-w-xl animate-fade-in-up stagger-2 font-medium">
              {fees.freeTrial
                ? 'The Global AI Real Estate Network — currently in free trial. Buyers, sellers and Technology Service Partners get AI-Powered Search Discovery, Pulse scoring and market intelligence with no platform fees.'
                : 'The AI Real Estate Network for AI-Powered Search Discovery between buyers, sellers and Technology Service Partners. AI-graded Pulse scores, valuations, floor plans and market intelligence — across UK, India and Canada.'}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up stagger-3">
              <Link to="/login">
                <Button className="bg-[#E07A3A] text-white hover:bg-[#C4652E] px-10 py-7 text-base tracking-widest uppercase font-semibold shadow-lg" data-testid="hero-explore-btn">
                  Explore Collection — Sign In
                  <ArrowRight className="ml-3 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/register?role=seller">
                <Button className="bg-[#1A1A1A] text-white hover:bg-[#333333] px-10 py-7 text-base tracking-widest uppercase font-semibold shadow-lg" data-testid="hero-list-btn">
                  Create Digital Property Profile
                </Button>
              </Link>
            </div>

            {/* Estate-agent-only CTA — surfaces the lead-gen offer
                exclusively to logged-in agents; everyone else (buyers,
                tenants, sellers, professionals, anonymous visitors)
                never sees this line. */}
            {isEstateAgent && (
              <Link
                to="/for-agents"
                className="mt-6 inline-flex items-center gap-2 text-[#E07A3A] hover:text-[#C4652E] text-sm tracking-widest uppercase font-semibold transition-colors"
                data-testid="hero-for-agents-link"
              >
                Technology Service Partner? See how Shopperden AI routes qualified leads to you
                <ArrowRight className="h-4 w-4" />
              </Link>
            )}

            {/* Pulse Investment Report CTA — paid lead magnet.
                Promoted to an emerald pill with a soft blink to draw the
                eye without screaming "ad". Pure-CSS keyframes (no JS) so
                the animation stays GPU-cheap on mobile. */}
            <div className="mt-4">
              <Link
                to="/free-valuation"
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#10B981] text-white hover:bg-[#059669] hover:text-white text-sm tracking-widest uppercase font-bold shadow-md hover:shadow-lg rounded transition-all pulse-report-blink"
                data-testid="hero-pulse-report-link"
              >
                <Sparkles className="h-4 w-4" />
                Get a Pulse Investment Report — £49 / ₹4,900 / C$79
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 border-t border-slate-200 bg-white/95 backdrop-blur-xl">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="grid grid-cols-3 gap-8">
              <div className="text-center" data-testid="stat-properties">
                <p className="text-3xl sm:text-4xl font-['Playfair_Display'] text-[#E07A3A] font-semibold">{liveStats.properties > 0 ? `${liveStats.properties}+` : '...'}</p>
                <p className="text-[#1A1A1A]/60 text-sm tracking-widest uppercase mt-1 font-medium">Live Properties</p>
              </div>
              <div className="text-center border-x border-slate-200" data-testid="stat-users">
                <p className="text-3xl sm:text-4xl font-['Playfair_Display'] text-[#E07A3A] font-semibold">{liveStats.users > 0 ? `${liveStats.users}+` : '...'}</p>
                <p className="text-[#1A1A1A]/60 text-sm tracking-widest uppercase mt-1 font-medium">Registered Users</p>
              </div>
              <div className="text-center" data-testid="stat-countries">
                <p className="text-3xl sm:text-4xl font-['Playfair_Display'] text-[#E07A3A] font-semibold">3</p>
                <p className="text-[#1A1A1A]/60 text-sm tracking-widest uppercase mt-1 font-medium">Global Markets</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Got our WhatsApp? — claim funnel for static-URL outreach recipients */}
      <GotOurWhatsAppBanner />

      {/* Features Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 border-b border-slate-100">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-sm tracking-[0.3em] text-[#E07A3A] mb-4 block uppercase font-semibold">Why Choose Us</span>
            <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl text-[#1A1A1A] font-semibold">The Art of Luxury Living</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: <Sparkles className="h-7 w-7" />, title: "AI Valuations", description: "Intelligent pricing insights powered by advanced AI to maximise your property's potential." },
              { icon: <MapPin className="h-7 w-7" />, title: "360° Virtual Tours", description: "Explore properties in stunning detail with immersive panoramic experiences." },
              { icon: <Award className="h-7 w-7" />, title: "Premium Service", description: "White-glove service with dedicated support for discerning clients." }
            ].map((feature, index) => (
              <div key={feature.title} className="luxury-card p-8 animate-fade-in-up" style={{ animationDelay: `${index * 0.15}s` }}>
                <div className="w-16 h-16 border border-[#E07A3A]/20 bg-[#FEF3EC] flex items-center justify-center text-[#E07A3A] mb-6">{feature.icon}</div>
                <h3 className="font-['Playfair_Display'] text-xl sm:text-2xl text-[#1A1A1A] mb-3 font-semibold">{feature.title}</h3>
                <p className="text-[#1A1A1A]/70 text-base leading-relaxed font-medium">{feature.description}</p>
              </div>
            ))}
          </div>

          {/* Phase A: New Features */}
          <div className="grid md:grid-cols-5 gap-6 mt-12">
            {[
              { icon: <Scale className="h-6 w-6" />, title: "Live Legal Progress", description: "Track every step of your transaction — from identity checks to completion — with country-specific milestones for UK, India & Canada." },
              { icon: <TrendingUp className="h-6 w-6" />, title: "Market Pricing Trends", description: "AI-powered area analysis. Search any postcode for average prices, demand levels, and expert tips." },
              { icon: <Shield className="h-6 w-6" />, title: "Verified Offers", description: "Buyers with confirmed mortgage or proof of funds get a Gold-Certified badge. No ghosting, no time-wasters." },
              { icon: <Bell className="h-6 w-6" />, title: "Automated Nudge Engine", description: "Deals stalling? Our smart reminders keep all parties moving — 24h gentle, 48h urgent. Faster completions." },
              { icon: <Wand2 className="h-6 w-6" />, title: "AI Floor Plan Generator", description: "Define your rooms and dimensions — our AI creates a professional 2D architectural floor plan in seconds. No surveyor needed." },
            ].map((f, i) => (
              <div key={f.title} className={`p-6 border bg-white hover:border-[#E07A3A]/30 transition-colors animate-fade-in-up ${f.title === 'AI Floor Plan Generator' ? 'border-[#E07A3A]/20 bg-[#FEF3EC]/30' : 'border-slate-100'}`} style={{ animationDelay: `${(i + 3) * 0.1}s` }}>
                <div className={`w-12 h-12 border flex items-center justify-center mb-4 ${f.title === 'AI Floor Plan Generator' ? 'border-[#E07A3A]/30 bg-[#E07A3A]/10 text-[#E07A3A]' : 'border-[#E07A3A]/20 bg-[#FEF3EC] text-[#E07A3A]'}`}>{f.icon}</div>
                <h3 className="font-['Playfair_Display'] text-lg text-[#1A1A1A] mb-2 font-semibold">{f.title}</h3>
                <p className="text-[#1A1A1A]/60 text-sm leading-relaxed">{f.description}</p>
                {f.title === 'AI Floor Plan Generator' && (
                  <div className="flex items-center gap-1.5 mt-3 text-[#E07A3A] text-xs font-semibold">
                    <Sparkles className="h-3 w-3" />
                    <span>NEW — Powered by GPT Image</span>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Live Exchange Rates badge */}
          <div className="mt-8 text-center">
            <div className="inline-flex items-center gap-3 px-6 py-3 bg-[#FEF3EC] border border-[#E07A3A]/20 rounded-full">
              <CreditCard className="h-5 w-5 text-[#E07A3A]" />
              <span className="text-sm font-medium text-[#1A1A1A]/80">Live Currency Conversion — Prices shown in £, ₹ and C$ powered by Fixer.io</span>
            </div>
          </div>
        </div>
      </section>

      {/* Trustpilot Reviews */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-white" data-testid="home-trustpilot-section">
        <div className="max-w-5xl mx-auto">
          <TrustpilotWidget />
        </div>
      </section>

      {/* Fresh harvested leads — Global Engine showcase */}
      <FreshLeadsCarousel />

      {/* Role router — funnels first-time visitors into the right SEO landing.
          This is the single highest-ROI block on the homepage: it cuts the
          "what is Shopperden for me?" question in one tap. */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white border-b border-slate-100" data-testid="home-role-router">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <span className="text-xs tracking-[0.3em] text-[#E07A3A] uppercase font-semibold block mb-2">Built for every role</span>
            <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl text-[#0F172A]">Pick your path</h2>
            <p className="text-sm text-slate-500 mt-2 max-w-xl mx-auto">
              Six tailored experiences — same trust, same flat connection fee.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4" data-testid="home-role-router-grid">
            {[
              { slug: 'buyers',         h: 'For Buyers',       sub: 'Direct-from-seller listings + AI matches' },
              { slug: 'sellers',        h: 'For Sellers',      sub: 'List free · keep your sale price' },
              { slug: 'tenants',        h: 'For Tenants',      sub: 'Direct landlords · no admin fees' },
              { slug: 'agents',         h: 'For Agents',       sub: 'Earn 20% on every referral' },
              { slug: 'professionals',  h: 'For Pros',         sub: 'Solicitors · brokers · property managers' },
              { slug: 'nri',            h: 'For NRIs',         sub: 'Buy in India from UK/Canada' },
            ].map((r) => (
              <Link
                key={r.slug}
                to={`/for/${r.slug}`}
                className="group border border-slate-200 rounded p-4 sm:p-5 hover:border-[#E07A3A] hover:shadow-md transition-all bg-white text-left"
                data-testid={`home-role-card-${r.slug}`}
              >
                <p className="font-['Playfair_Display'] text-lg sm:text-xl text-[#0F172A] group-hover:text-[#E07A3A]">{r.h}</p>
                <p className="text-xs sm:text-sm text-slate-500 mt-1 leading-snug">{r.sub}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Regional Hubs — NRI corridor shortcuts. */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-[#FEF8EC]/40" data-testid="home-region-router">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-end justify-between gap-3 flex-wrap mb-6">
            <div>
              <span className="text-xs tracking-[0.3em] text-[#E07A3A] uppercase font-semibold block mb-1">NRI corridors</span>
              <h2 className="font-['Playfair_Display'] text-2xl text-[#0F172A]">Browse pros by region</h2>
            </div>
            <Link to="/find-a-professional" className="text-xs font-semibold text-[#E07A3A] hover:underline">All cities →</Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="home-region-grid">
            {[
              { slug: 'punjab', name: 'Punjab & Chandigarh', flag: '🇮🇳', sub: 'NRI focus: UK & Canada diaspora' },
              { slug: 'london-greater', name: 'Greater London', flag: '🇬🇧', sub: '32 boroughs · top-rated agents' },
              { slug: 'ontario', name: 'Ontario (GTA)', flag: '🇨🇦', sub: 'Toronto · Mississauga · Brampton' },
            ].map((r) => (
              <Link
                key={r.slug}
                to={`/region/${r.slug}`}
                className="group bg-white border border-slate-200 rounded p-5 hover:border-[#E07A3A] hover:shadow-md transition-all"
                data-testid={`home-region-card-${r.slug}`}
              >
                <p className="text-2xl mb-2">{r.flag}</p>
                <p className="font-['Playfair_Display'] text-lg text-[#0F172A] group-hover:text-[#E07A3A]">{r.name}</p>
                <p className="text-xs text-slate-500 mt-1">{r.sub}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Government Resources */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-[#F8FAFC]" data-testid="home-govt-links-section">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-6">
            <span className="text-xs tracking-[0.3em] text-[#E07A3A] uppercase font-semibold block mb-2">Official Resources</span>
            <h2 className="font-['Playfair_Display'] text-2xl sm:text-3xl text-[#1A1A1A] font-semibold">Government Property Checks</h2>
            <p className="text-sm text-slate-400 mt-2">Quick links to verify ownership, tax, and legal details. Opens in a new tab.</p>
          </div>
          <GovtLinksPanel country={fees.country || 'UK'} />
        </div>
      </section>
    </div>
  );

  /* ───── PAGE 2: EXPLORE (Buyer Attraction + Featured Properties) ───── */
  const page2 = (
    <div>
      {/* Buyer Attraction Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-white to-[#F8FAFC]" data-testid="buyer-attraction-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-sm tracking-[0.3em] text-[#E07A3A] mb-4 block uppercase font-semibold">For Property Buyers</span>
            <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl lg:text-5xl text-[#0F172A] font-semibold leading-tight">
              Smart Tools for<span className="text-[#E07A3A] italic"> Smarter Decisions</span>
            </h2>
            <p className="text-[#1A1A1A]/50 mt-4 max-w-2xl mx-auto text-base font-medium">
              Go beyond browsing. Explore properties in immersive 360°, get AI-powered valuations, and use built-in financial calculators — all before you even schedule a viewing.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="group relative p-8 bg-white border border-slate-100 hover:border-[#E07A3A]/30 transition-all duration-300 hover:shadow-lg" data-testid="buyer-feature-360">
              <div className="w-14 h-14 mb-6 bg-[#FEF3EC] flex items-center justify-center group-hover:scale-110 transition-transform">
                <svg className="h-7 w-7 text-[#E07A3A]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                  <path d="M2 12h20" />
                </svg>
              </div>
              <h3 className="font-['Playfair_Display'] text-lg sm:text-xl text-[#0F172A] font-semibold mb-3">360° Immersive Tours</h3>
              <p className="text-[#1A1A1A]/50 text-base sm:text-sm leading-relaxed font-medium mb-4">Walk through properties from anywhere in the world. Our panoramic viewer lets you explore every room, angle, and detail as if you were standing right there.</p>
              <div className="flex items-center gap-2 text-[#E07A3A] text-sm font-semibold"><span>Available on all listings</span><ArrowRight className="h-3 w-3" /></div>
            </div>
            <div className="group relative p-8 bg-white border border-slate-100 hover:border-[#E07A3A]/30 transition-all duration-300 hover:shadow-lg" data-testid="buyer-feature-ai">
              <div className="w-14 h-14 mb-6 bg-blue-50 flex items-center justify-center group-hover:scale-110 transition-transform">
                <Sparkles className="h-7 w-7 text-blue-600" />
              </div>
              <h3 className="font-['Playfair_Display'] text-xl text-[#0F172A] font-semibold mb-3">AI-Powered Valuation</h3>
              <p className="text-[#1A1A1A]/50 text-sm leading-relaxed font-medium mb-4">Instantly generate premium profit analysis powered by artificial intelligence. Understand market value, investment potential, and key value drivers before making an offer.</p>
              <div className="flex items-center gap-2 text-blue-600 text-sm font-semibold"><span>Powered by Gemini AI</span><ArrowRight className="h-3 w-3" /></div>
            </div>
            <div className="group relative p-8 bg-white border border-slate-100 hover:border-[#E07A3A]/30 transition-all duration-300 hover:shadow-lg" data-testid="buyer-feature-calculators">
              <div className="w-14 h-14 mb-6 bg-emerald-50 flex items-center justify-center group-hover:scale-110 transition-transform">
                <Search className="h-7 w-7 text-emerald-600" />
              </div>
              <h3 className="font-['Playfair_Display'] text-xl text-[#0F172A] font-semibold mb-3">Built-In Financial Tools</h3>
              <p className="text-[#1A1A1A]/50 text-sm leading-relaxed font-medium mb-4">Mortgage affordability calculator and stamp duty tracker right on every listing. Know your monthly payments and total costs instantly — no spreadsheets needed.</p>
              <div className="flex items-center gap-2 text-emerald-600 text-sm font-semibold"><span>UK, India & Canada markets supported</span><ArrowRight className="h-3 w-3" /></div>
            </div>
          </div>
          <div className="text-center mt-14">
            <Link to="/properties">
              <Button className="bg-[#0F172A] text-white hover:bg-[#1E293B] px-10 py-6 text-sm tracking-widest uppercase font-semibold" data-testid="buyer-cta-btn">
                Browse Properties <ArrowRight className="ml-3 h-4 w-4" />
              </Button>
            </Link>
            <p className="text-[#1A1A1A]/30 text-xs mt-3 tracking-wider font-medium">Free to browse. Register to unlock full features.</p>
          </div>
        </div>
      </section>

      {/* Featured Properties */}
      <section className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end gap-4 mb-12">
            <div>
              <span className="text-sm tracking-[0.3em] text-[#E07A3A] mb-4 block uppercase font-semibold">Featured Collection</span>
              <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl text-[#1A1A1A] font-semibold">Handpicked Properties</h2>
            </div>
            <Link to="/properties">
              <Button variant="ghost" className="text-slate-500 hover:text-[#E07A3A] hover:bg-transparent tracking-widest text-sm uppercase" data-testid="view-all-properties-btn">
                View All <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="luxury-card h-96 animate-pulse">
                  <div className="h-64 bg-slate-100" />
                  <div className="p-5 space-y-3"><div className="h-4 bg-slate-100 w-1/3" /><div className="h-4 bg-slate-100 w-3/4" /></div>
                </div>
              ))}
            </div>
          ) : featuredProperties.length > 0 ? (
            <div className="property-grid">
              {featuredProperties.map((property, index) => (
                <div key={property.id} className="animate-fade-in-up" style={{ animationDelay: `${index * 0.1}s` }}>
                  <PropertyCard property={property} />
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-20 luxury-card">
              <Shield className="h-16 w-16 text-slate-200 mx-auto mb-4" />
              <h3 className="text-xl text-[#1A1A1A] mb-2 font-['Playfair_Display'] font-semibold">No Properties Yet</h3>
              <p className="text-[#1A1A1A]/60 mb-6 text-base font-medium">Start listing properties on Shopperden</p>
              <Link to="/register">
                <Button className="bg-[#E07A3A] text-white hover:bg-[#C4652E] tracking-widest text-sm uppercase">Create Digital Property Profile</Button>
              </Link>
            </div>
          )}
        </div>
      </section>
    </div>
  );

  /* ───── PAGE 4: FOR SELLERS & PROS ───── */
  const page4 = (
    <div>
      {/* Seller Value Proposition */}
      {sellerContent && sellerContent.enabled !== false && (
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-[#0F172A] relative overflow-hidden" data-testid="seller-value-section">
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '40px 40px' }} />
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <span className="text-sm tracking-[0.3em] text-[#E07A3A] mb-4 block uppercase font-semibold">For Property Sellers</span>
            <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl lg:text-5xl text-white font-semibold leading-tight">
              {sellerContent.headline}<br />
              <span className="text-[#E07A3A] italic">When {fees.currency}{fees.seller} Gets You the Right Buyer?</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto mb-20 relative">
            {fees.freeTrial && (
              <div className="absolute inset-0 z-20 flex items-center justify-center">
                <div className="bg-green-500 text-white px-8 py-4 text-center shadow-2xl shadow-green-500/30">
                  <Gift className="h-8 w-8 mx-auto mb-2" />
                  <p className="font-bold text-lg tracking-wider uppercase">Free Trial Active</p>
                  <p className="text-sm text-white/80 mt-1">All fees waived — no charges during trial</p>
                </div>
              </div>
            )}
            <div className={`border border-white/10 bg-white/[0.03] p-8 relative ${fees.freeTrial ? 'blur-sm' : ''}`} data-testid="comparison-traditional">
              <div className="absolute top-0 right-0 bg-red-500/90 text-white text-xs font-bold tracking-wider uppercase px-4 py-1.5">Traditional</div>
              <p className="text-white/40 text-xs tracking-[0.2em] uppercase mb-3 font-semibold">{sellerContent.traditional.label}</p>
              <p className="font-['Playfair_Display'] text-5xl sm:text-6xl text-white/80 font-semibold mb-1">
                {fees.currency}{fees.country === 'IN' ? '50,000' : fees.country === 'CA' ? '5,000' : '5,000'}<span className="text-lg text-white/30">+</span>
              </p>
              <p className="text-white/30 text-sm mb-6 font-medium">{sellerContent.traditional.price_note}</p>
              <div className="space-y-3">
                {sellerContent.traditional.points.map((item, i) => (
                  <div key={`trad-${item.slice(0, 20)}`} className="flex items-start gap-3">
                    <XCircle className="h-4 w-4 text-red-400/70 mt-0.5 flex-shrink-0" />
                    <span className="text-white/40 text-sm font-medium">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className={`border-2 border-[#E07A3A]/50 bg-[#E07A3A]/[0.06] p-8 relative ${fees.freeTrial ? 'blur-sm' : ''}`} data-testid="comparison-shopperden">
              <div className="absolute top-0 right-0 bg-[#E07A3A] text-white text-xs font-bold tracking-wider uppercase px-4 py-1.5">Shopperden</div>
              <p className="text-[#E07A3A]/70 text-xs tracking-[0.2em] uppercase mb-3 font-semibold">{sellerContent.shopperden.label}</p>
              <p className="font-['Playfair_Display'] text-5xl sm:text-6xl text-[#E07A3A] font-semibold mb-1">{fees.currency}{fees.seller}</p>
              <p className="text-white/40 text-sm mb-6 font-medium">{sellerContent.shopperden.price_note}</p>
              <div className="space-y-3">
                {sellerContent.shopperden.points.map((item, i) => (
                  <div key={`spd-${item.slice(0, 20)}`} className="flex items-start gap-3">
                    <CheckCircle className="h-4 w-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                    <span className="text-white/70 text-sm font-medium">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className={`text-center mb-20 ${fees.freeTrial ? 'blur-sm' : ''}`}>
            <div className="inline-flex items-center gap-4 bg-emerald-500/10 border border-emerald-500/20 px-8 py-4" data-testid="savings-highlight">
              <span className="text-emerald-400 font-['Playfair_Display'] text-3xl sm:text-4xl font-semibold">{fees.currency}{fees.seller} vs {fees.currency}{fees.country === 'IN' ? '50,000' : '5,000'}+</span>
              <div className="text-left">
                <p className="text-white text-sm font-semibold">Huge Savings</p>
                <p className="text-white/40 text-xs font-medium">Save thousands with Shopperden</p>
              </div>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {sellerContent.pillars.map((pillar, index) => {
              const accents = ['#22C55E', '#3B82F6', '#E07A3A'];
              const iconMap = { 0: CreditCard, 1: Shield, 2: Users };
              const IconComp = iconMap[index % 3];
              const accent = accents[index % 3];
              return (
                <div key={pillar.title} className="text-center p-8 border border-white/[0.06] bg-white/[0.02] hover:border-white/15 transition-all duration-300" data-testid={`seller-pillar-${index}`}>
                  <div className="w-14 h-14 mx-auto mb-5 flex items-center justify-center border" style={{ borderColor: `${accent}30`, backgroundColor: `${accent}10`, color: accent }}>
                    <IconComp className="h-6 w-6" />
                  </div>
                  <h3 className="font-['Playfair_Display'] text-xl text-white mb-3 font-semibold">{pillar.title}</h3>
                  <p className="text-white/45 text-sm leading-relaxed font-medium">{pillar.description}</p>
                </div>
              );
            })}
          </div>
          {/* AI Floor Plan Highlight for Sellers */}
          <div className="max-w-4xl mx-auto mb-16 border border-[#E07A3A]/30 bg-[#E07A3A]/[0.04] p-8 relative overflow-hidden" data-testid="seller-floorplan-highlight">
            <div className="absolute top-0 right-0 bg-[#E07A3A] text-white text-[10px] font-bold tracking-[0.2em] uppercase px-4 py-1.5">New Feature</div>
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="w-20 h-20 bg-[#E07A3A]/10 border border-[#E07A3A]/20 flex items-center justify-center flex-shrink-0">
                <Wand2 className="h-10 w-10 text-[#E07A3A]" />
              </div>
              <div className="flex-1 text-center md:text-left">
                <h3 className="font-['Playfair_Display'] text-2xl text-white font-semibold mb-2">AI Floor Plan Generator</h3>
                <p className="text-white/50 text-sm leading-relaxed font-medium mb-4">
                  Skip the wait and cost of commissioning a surveyor for a basic floor plan ({fees.currency}{fees.country === 'IN' ? '5,000' : fees.country === 'CA' ? '200' : '150'}+).
                  Upload a rough sketch, define your rooms, and our AI redraws a professional 2D architect-style plan in seconds.
                  Buyers rank floor plans as the <span className="text-[#E07A3A] font-semibold">#1 most useful feature</span> on a listing.
                </p>
                <div className="flex flex-wrap gap-4 justify-center md:justify-start">
                  <div className="flex items-center gap-2 text-white/60 text-xs">
                    <CheckCircle className="h-3.5 w-3.5 text-emerald-400" />
                    <span>Room-by-room AI guide</span>
                  </div>
                  <div className="flex items-center gap-2 text-white/60 text-xs">
                    <CheckCircle className="h-3.5 w-3.5 text-emerald-400" />
                    <span>GPT-powered image generation</span>
                  </div>
                  <div className="flex items-center gap-2 text-white/60 text-xs">
                    <CheckCircle className="h-3.5 w-3.5 text-emerald-400" />
                    <span>Download & attach to listing</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="text-center">
            <Link to="/register">
              <Button className="bg-[#E07A3A] text-white hover:bg-[#C4652E] px-12 py-7 text-base tracking-widest uppercase font-semibold shadow-lg shadow-[#E07A3A]/20" data-testid="seller-cta-btn">
                {sellerContent.cta_text} <ArrowRight className="ml-3 h-5 w-5" />
              </Button>
            </Link>
            <p className="text-white/30 text-xs mt-4 tracking-wider font-medium">{sellerContent.cta_subtitle}</p>
          </div>
        </div>
      </section>
      )}

      {/* Savings Calculator — Sellers Only */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white border-b border-slate-100">
        <SavingsCalculator fees={fees} calcPrice={calcPrice} setCalcPrice={setCalcPrice} />
      </section>

      {/* Professional Section */}
      {proContent && proContent.enabled !== false && (
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-white relative overflow-hidden" data-testid="professional-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            {proContent.badge && (
              <span className="inline-block bg-[#0F172A] text-white text-xs tracking-[0.25em] uppercase font-bold px-5 py-2 mb-6" data-testid="pro-badge">{proContent.badge}</span>
            )}
            <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl lg:text-5xl text-[#0F172A] font-semibold leading-tight">{proContent.headline}</h2>
            <p className="text-[#E07A3A] font-['Playfair_Display'] text-xl sm:text-2xl italic mt-2 font-semibold">{proContent.headline_accent}</p>
            <p className="text-[#1A1A1A]/50 mt-6 max-w-3xl mx-auto text-base leading-relaxed font-medium" data-testid="pro-intro">{proContent.intro}</p>
          </div>
          {proContent.stats && proContent.stats.length > 0 && (
            <div className="grid md:grid-cols-3 gap-0 max-w-4xl mx-auto mb-20 border border-slate-200 divide-x divide-slate-200" data-testid="pro-stats-row">
              {proContent.stats.map((stat, i) => {
                const dynamicValue = i === 1 ? `${fees.currency}${fees.pro}` : stat.value;
                return (
                  <div key={stat.label} className="p-8 text-center" data-testid={`pro-stat-${i}`}>
                    <p className="font-['Playfair_Display'] text-4xl sm:text-5xl text-[#E07A3A] font-semibold mb-2">{dynamicValue}</p>
                    <p className="text-[#0F172A] text-sm font-bold tracking-wider uppercase mb-2">{stat.label}</p>
                    <p className="text-[#1A1A1A]/40 text-xs leading-relaxed font-medium">{stat.description}</p>
                  </div>
                );
              })}
            </div>
          )}
          {proContent.benefits && proContent.benefits.length > 0 && (
            <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto mb-16">
              {proContent.benefits.map((benefit, i) => {
                const iconMap = { 0: Shield, 1: FileCheck, 2: Users, 3: Briefcase };
                const IconComp = iconMap[i % 4];
                const colors = ['#E07A3A', '#3B82F6', '#22C55E', '#8B5CF6'];
                const color = colors[i % 4];
                return (
                  <div key={benefit.title} className="flex gap-5 p-6 border border-slate-100 hover:border-slate-200 transition-colors" data-testid={`pro-benefit-${i}`}>
                    <div className="w-11 h-11 flex-shrink-0 flex items-center justify-center" style={{ backgroundColor: `${color}10`, color }}><IconComp className="h-5 w-5" /></div>
                    <div>
                      <h4 className="text-[#0F172A] font-semibold text-sm mb-1">{benefit.title}</h4>
                      <p className="text-[#1A1A1A]/45 text-sm leading-relaxed font-medium">{benefit.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <div className="text-center">
            <Link to="/register">
              <Button className="bg-[#0F172A] text-white hover:bg-[#1E293B] px-12 py-7 text-base tracking-widest uppercase font-semibold" data-testid="pro-cta-btn">
                {proContent.cta_text} <ArrowRight className="ml-3 h-5 w-5" />
              </Button>
            </Link>
            <p className="text-[#1A1A1A]/30 text-xs mt-4 tracking-wider font-medium">{proContent.cta_subtitle}</p>
          </div>
        </div>
      </section>
      )}
    </div>
  );

  /* ───── PAGE 5: ABOUT & MORE ───── */
  const page5 = (
    <div>
      {/* Why SHOPPERDEN */}
      <WhyShopperdenSection whyContent={whyContent} fees={fees} />

      {/* Find a Professional — directory promo */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <FindProfessionalPromo country={fees.country || 'UK'} />
      </section>

      {/* Ad Banners */}
      <AdBannersSection adBanners={adBanners} adsenseConfig={adsenseConfig} trackBannerClick={trackBannerClick} />

      {/* Testimonials */}
      <TestimonialsSection />

      {/* Trust Seals */}
      <TrustSeals liveStats={liveStats} />

      {/* Trustpilot Reviews */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-5xl mx-auto">
          <TrustpilotWidget />
        </div>
      </section>

      {/* Government Resources */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-[#F8FAFC]">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-6">
            <span className="text-xs tracking-[0.3em] text-[#E07A3A] uppercase font-semibold block mb-2">Official Resources</span>
            <h2 className="font-['Playfair_Display'] text-2xl sm:text-3xl text-[#1A1A1A] font-semibold">Government Property Checks</h2>
            <p className="text-sm text-slate-400 mt-2">Quick links to verify ownership, tax, and legal details. Opens in a new tab.</p>
          </div>
          <GovtLinksPanel country={fees.country || 'UK'} />
        </div>
      </section>

      {/* CTA Section */}
      <LandingCTA />

      {/* Soro Blog Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8">
            <span className="text-xs tracking-[0.3em] text-[#E07A3A] uppercase font-semibold block mb-2">Latest Insights</span>
            <h2 className="font-['Playfair_Display'] text-2xl sm:text-3xl text-[#1A1A1A] font-semibold">Property Tips & Guides</h2>
          </div>
          <div id="soro-blog-landing" data-testid="soro-blog-landing"></div>
        </div>
      </section>

      {/* Footer */}
      <AppDownloadBanner />
      <LandingFooter />
    </div>
  );

  // Only show the "For Sellers" book-page to logged-in sellers. Every
  // other visitor (buyers, tenants, agents, pros, anonymous) sees a 3-
  // page book instead — they can still reach the seller pitch via the
  // SEO landing at /for/sellers, the Header nav and the footer link.
  const isSeller = (user?.role || '').toLowerCase() === 'seller';
  const allPages = isSeller ? [page1, page2, page4, page5] : [page1, page2, page5];
  const pageLabels = isSeller
    ? ['Home', 'Explore', 'For Sellers', 'About']
    : ['Home', 'Explore', 'About'];

  const [howItWorksOpen, setHowItWorksOpen] = useState(false);

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {!doorComplete && <DoorOpening onComplete={handleDoorComplete} />}
      <TrustStrip />
      <Header />
      <BookLayout pages={allPages} pageLabels={pageLabels} />

      {/* Floating "How It Works" Tab */}
      <button
        onClick={() => setHowItWorksOpen(true)}
        className="fixed right-0 top-1/2 -translate-y-1/2 z-[60] bg-[#0F172A] text-white px-3 py-6 shadow-xl hover:bg-[#1e293b] transition-all duration-300 hover:px-4 group"
        style={{ writingMode: 'vertical-rl', textOrientation: 'mixed', borderRadius: '8px 0 0 8px' }}
        data-testid="how-it-works-tab"
      >
        <span className="text-xs font-semibold tracking-[0.15em] uppercase flex items-center gap-2">
          <Wand2 className="h-4 w-4 rotate-90 group-hover:text-[#E07A3A] transition-colors" />
          How It Works
        </span>
      </button>

      {/* Floating "AI Network" Tab — mirrors How It Works on the LEFT */}
      <AINetworkTab dataTestId="ai-network-tab" />

      {/* How It Works Slide-out Panel */}
      {howItWorksOpen && (
        <div className="fixed inset-0 z-[70]" data-testid="how-it-works-overlay">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setHowItWorksOpen(false)}
            style={{ animation: 'fadeIn 0.3s ease-out' }}
          />
          {/* Panel */}
          <div
            className="absolute right-0 top-0 h-full w-full max-w-3xl bg-[#0F172A] overflow-y-auto shadow-2xl"
            style={{ animation: 'slideInRight 0.35s cubic-bezier(0.4, 0, 0.2, 1)' }}
            data-testid="how-it-works-panel"
          >
            <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 border-b border-white/10 bg-[#0F172A]/95 backdrop-blur-md">
              <div>
                <span className="text-xs tracking-[0.3em] text-[#E07A3A] uppercase font-semibold">Simple Process</span>
                <h2 className="font-['Playfair_Display'] text-xl text-white font-semibold">How It Works</h2>
              </div>
              <button
                onClick={() => setHowItWorksOpen(false)}
                className="w-10 h-10 flex items-center justify-center text-white/50 hover:text-white hover:bg-white/10 transition-colors"
                data-testid="how-it-works-close"
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="px-6 py-10">
              <p className="text-white/50 text-center text-sm mb-10 max-w-lg mx-auto font-medium">
                A clear, secure journey tailored to your role — whether you're buying, renting, selling, or advising
              </p>
              <HowItWorksFlows />
            </div>
          </div>
        </div>
      )}

      {/* Animations */}
      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideInRight { from { transform: translateX(100%); } to { transform: translateX(0); } }
        @keyframes pulseReportBlink {
          0%, 100% {
            opacity: 1;
            box-shadow: 0 4px 14px -2px rgba(16, 185, 129, 0.45),
                        0 0 0 0 rgba(16, 185, 129, 0.6);
          }
          50% {
            opacity: 0.88;
            box-shadow: 0 4px 14px -2px rgba(16, 185, 129, 0.55),
                        0 0 0 10px rgba(16, 185, 129, 0);
          }
        }
        .pulse-report-blink {
          animation: pulseReportBlink 1.6s ease-in-out infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .pulse-report-blink { animation: none; }
        }
      `}</style>
    </div>
  );
};

export default LandingPage;
